import moment from 'moment';
import { electronicFormatIBAN, isValidIBAN } from 'ibantools';
export const fetchPortalURL = (isDevelopment, isStaging, subdomain = 'nickel', customDomain) => {
    if (customDomain) {
        return `https://${customDomain}`;
    }
    if (isDevelopment) {
        return `http://${subdomain}.localhost:3001`;
    }
    else if (isStaging) {
        return `https://${subdomain}.staging.nickelpayments.com`;
    }
    else {
        return `https://${subdomain}.nickelpayments.com`;
    }
};
/**
 * The organization's own payment portal origin, or null when it has neither a
 * custom domain nor a subdomain. Callers must not substitute another portal:
 * a checkout path resolves against whichever organization owns the host.
 */
export const merchantPortalURL = (isDevelopment, isStaging, host) => {
    if (host === null || host === void 0 ? void 0 : host.customDomain) {
        return fetchPortalURL(isDevelopment, isStaging, '', host.customDomain);
    }
    if (!(host === null || host === void 0 ? void 0 : host.subdomain)) {
        return null;
    }
    return fetchPortalURL(isDevelopment, isStaging, host.subdomain);
};
export const getFrequencyString = (frequency, startDateEpochMillis) => {
    const startDateMoment = moment(startDateEpochMillis);
    const day = startDateMoment.format('Do');
    const monthName = startDateMoment.format('MMM');
    const weekday = startDateMoment.format('ddd');
    switch (frequency) {
        case 'WEEKLY':
            return `Every week on ${weekday}`;
        case 'FORTNIGHTLY':
            return `Every 2 weeks on ${weekday}`;
        case 'MONTHLY':
            return `Every month on the ${day}`;
        case 'QUARTERLY':
            return `Every quarter`;
        case 'YEARLY':
            return `Every year on ${monthName} ${day}`;
        default:
            return 'Recurring';
    }
};
export const countryCodeAlpha3Map = {
    USA: 'United States',
    CAN: 'Canada',
    MEX: 'Mexico',
    GBR: 'United Kingdom',
    FRA: 'France',
    DEU: 'Germany',
    ITA: 'Italy',
    ESP: 'Spain',
    PRT: 'Portugal',
    NLD: 'Netherlands',
    BEL: 'Belgium',
    CHE: 'Switzerland',
    AUT: 'Austria',
    IRL: 'Ireland',
    DNK: 'Denmark',
    SWE: 'Sweden',
    NOR: 'Norway',
    FIN: 'Finland',
    POL: 'Poland',
    CZE: 'Czech Republic',
    HUN: 'Hungary',
    GRC: 'Greece',
    ROU: 'Romania',
    BGR: 'Bulgaria',
    HRV: 'Croatia',
    SVN: 'Slovenia',
    SVK: 'Slovakia',
    EST: 'Estonia',
    LVA: 'Latvia',
    LTU: 'Lithuania',
    RUS: 'Russia',
    UKR: 'Ukraine',
    CHN: 'China',
    JPN: 'Japan',
    IND: 'India',
    KOR: 'South Korea',
    TWN: 'Taiwan',
    IDN: 'Indonesia',
    THA: 'Thailand',
    VNM: 'Vietnam',
    MYS: 'Malaysia',
    SGP: 'Singapore',
    PHL: 'Philippines',
    PAK: 'Pakistan',
    BGD: 'Bangladesh',
    LKA: 'Sri Lanka',
    MMR: 'Myanmar',
    NPL: 'Nepal',
    KHM: 'Cambodia',
    LAO: 'Laos',
    MNG: 'Mongolia',
    KAZ: 'Kazakhstan',
    UZB: 'Uzbekistan',
    TKM: 'Turkmenistan',
    KGZ: 'Kyrgyzstan',
    TJK: 'Tajikistan',
    AFG: 'Afghanistan',
    IRN: 'Iran',
    IRQ: 'Iraq',
    TUR: 'Turkey',
    SAU: 'Saudi Arabia',
    ARE: 'United Arab Emirates',
    ISR: 'Israel',
    JOR: 'Jordan',
    LBN: 'Lebanon',
    SYR: 'Syria',
    YEM: 'Yemen',
    OMN: 'Oman',
    KWT: 'Kuwait',
    QAT: 'Qatar',
    BHR: 'Bahrain',
    AUS: 'Australia',
    NZL: 'New Zealand',
    FJI: 'Fiji',
    PNG: 'Papua New Guinea',
    ZAF: 'South Africa',
    EGY: 'Egypt',
    NGA: 'Nigeria',
    KEN: 'Kenya',
    ETH: 'Ethiopia',
    UGA: 'Uganda',
    TZA: 'Tanzania',
    GHA: 'Ghana',
    MAR: 'Morocco',
    DZA: 'Algeria',
    TUN: 'Tunisia',
    LBY: 'Libya',
    SDN: 'Sudan',
    AGO: 'Angola',
    MOZ: 'Mozambique',
    MDG: 'Madagascar',
    CMR: 'Cameroon',
    CIV: "Côte d'Ivoire",
    MLI: 'Mali',
    BFA: 'Burkina Faso',
    NER: 'Niger',
    TCD: 'Chad',
    SEN: 'Senegal',
    GIN: 'Guinea',
    SLE: 'Sierra Leone',
    LBR: 'Liberia',
    BRA: 'Brazil',
    ARG: 'Argentina',
    CHL: 'Chile',
    PER: 'Peru',
    COL: 'Colombia',
    VEN: 'Venezuela',
    ECU: 'Ecuador',
    BOL: 'Bolivia',
    PRY: 'Paraguay',
    URY: 'Uruguay',
    GUY: 'Guyana',
    SUR: 'Suriname',
    GTM: 'Guatemala',
    BLZ: 'Belize',
    SLV: 'El Salvador',
    HND: 'Honduras',
    NIC: 'Nicaragua',
    CRI: 'Costa Rica',
    PAN: 'Panama',
    CUB: 'Cuba',
    DOM: 'Dominican Republic',
    HTI: 'Haiti',
    JAM: 'Jamaica',
    TTO: 'Trinidad and Tobago',
    BHS: 'Bahamas',
    BRB: 'Barbados',
};
export function getCountryNameFromAlpha3Code(alpha3Code) {
    const upperCode = alpha3Code.toUpperCase();
    return countryCodeAlpha3Map[upperCode] || 'Unknown';
}
const countryCodeAlpha2ToAlpha3Map = {
    US: 'USA',
    CA: 'CAN',
    MX: 'MEX',
    BR: 'BRA',
    AR: 'ARG',
    CL: 'CHL',
    PE: 'PER',
    CO: 'COL',
    VE: 'VEN',
    EC: 'ECU',
    BO: 'BOL',
    PY: 'PRY',
    UY: 'URY',
    GY: 'GUY',
    SR: 'SUR',
    GT: 'GTM',
    BZ: 'BLZ',
    SV: 'SLV',
    HN: 'HND',
    NI: 'NIC',
    CR: 'CRI',
    PA: 'PAN',
    CU: 'CUB',
    DO: 'DOM',
    HT: 'HTI',
    JM: 'JAM',
    TT: 'TTO',
    BS: 'BHS',
    BB: 'BRB',
    AG: 'ATG',
    DM: 'DMA',
    GD: 'GRD',
    KN: 'KNA',
    LC: 'LCA',
    VC: 'VCT',
    PR: 'PRI',
    VI: 'VIR',
    VG: 'VGB',
    KY: 'CYM',
    TC: 'TCA',
    BM: 'BMU',
    AW: 'ABW',
    CW: 'CUW',
    SX: 'SXM',
    BQ: 'BES',
    AI: 'AIA',
    MS: 'MSR',
    GF: 'GUF',
    GP: 'GLP',
    MQ: 'MTQ',
    BL: 'BLM',
    MF: 'MAF',
    PM: 'SPM',
    FK: 'FLK',
    GS: 'SGS',
    GB: 'GBR',
    FR: 'FRA',
    DE: 'DEU',
    IT: 'ITA',
    ES: 'ESP',
    PT: 'PRT',
    NL: 'NLD',
    BE: 'BEL',
    CH: 'CHE',
    AT: 'AUT',
    IE: 'IRL',
    DK: 'DNK',
    SE: 'SWE',
    NO: 'NOR',
    FI: 'FIN',
    IS: 'ISL',
    LU: 'LUX',
    PL: 'POL',
    CZ: 'CZE',
    HU: 'HUN',
    GR: 'GRC',
    RO: 'ROU',
    BG: 'BGR',
    HR: 'HRV',
    SI: 'SVN',
    SK: 'SVK',
    EE: 'EST',
    LV: 'LVA',
    LT: 'LTU',
    CY: 'CYP',
    MT: 'MLT',
    AL: 'ALB',
    RS: 'SRB',
    ME: 'MNE',
    MK: 'MKD',
    BA: 'BIH',
    RU: 'RUS',
    UA: 'UKR',
    BY: 'BLR',
    MD: 'MDA',
    GE: 'GEO',
    AM: 'ARM',
    AZ: 'AZE',
    AD: 'AND',
    MC: 'MCO',
    SM: 'SMR',
    VA: 'VAT',
    LI: 'LIE',
    FO: 'FRO',
    GI: 'GIB',
    GL: 'GRL',
    GG: 'GGY',
    JE: 'JEY',
    IM: 'IMN',
    SJ: 'SJM',
    AX: 'ALA',
    CN: 'CHN',
    JP: 'JPN',
    IN: 'IND',
    KR: 'KOR',
    KP: 'PRK',
    ID: 'IDN',
    TH: 'THA',
    VN: 'VNM',
    MY: 'MYS',
    SG: 'SGP',
    PH: 'PHL',
    PK: 'PAK',
    BD: 'BGD',
    LK: 'LKA',
    MM: 'MMR',
    NP: 'NPL',
    KH: 'KHM',
    LA: 'LAO',
    MN: 'MNG',
    BN: 'BRN',
    TL: 'TLS',
    TW: 'TWN',
    HK: 'HKG',
    MO: 'MAC',
    MV: 'MDV',
    BT: 'BTN',
    KZ: 'KAZ',
    UZ: 'UZB',
    TM: 'TKM',
    KG: 'KGZ',
    TJ: 'TJK',
    AF: 'AFG',
    IR: 'IRN',
    IQ: 'IRQ',
    TR: 'TUR',
    SA: 'SAU',
    AE: 'ARE',
    IL: 'ISR',
    PS: 'PSE',
    JO: 'JOR',
    LB: 'LBN',
    SY: 'SYR',
    YE: 'YEM',
    OM: 'OMN',
    KW: 'KWT',
    QA: 'QAT',
    BH: 'BHR',
    AU: 'AUS',
    NZ: 'NZL',
    FJ: 'FJI',
    PG: 'PNG',
    WS: 'WSM',
    TO: 'TON',
    VU: 'VUT',
    SB: 'SLB',
    KI: 'KIR',
    MH: 'MHL',
    FM: 'FSM',
    PW: 'PLW',
    NR: 'NRU',
    TV: 'TUV',
    CK: 'COK',
    NU: 'NIU',
    TK: 'TKL',
    AS: 'ASM',
    GU: 'GUM',
    MP: 'MNP',
    PF: 'PYF',
    NC: 'NCL',
    WF: 'WLF',
    PN: 'PCN',
    NF: 'NFK',
    HM: 'HMD',
    CC: 'CCK',
    CX: 'CXR',
    ZA: 'ZAF',
    EG: 'EGY',
    NG: 'NGA',
    KE: 'KEN',
    ET: 'ETH',
    UG: 'UGA',
    TZ: 'TZA',
    GH: 'GHA',
    MA: 'MAR',
    DZ: 'DZA',
    TN: 'TUN',
    LY: 'LBY',
    SD: 'SDN',
    SS: 'SSD',
    AO: 'AGO',
    MZ: 'MOZ',
    MG: 'MDG',
    CM: 'CMR',
    CI: 'CIV',
    ML: 'MLI',
    BF: 'BFA',
    NE: 'NER',
    TD: 'TCD',
    SN: 'SEN',
    GN: 'GIN',
    SL: 'SLE',
    LR: 'LBR',
    RW: 'RWA',
    BI: 'BDI',
    CD: 'COD',
    CG: 'COG',
    GA: 'GAB',
    GQ: 'GNQ',
    CF: 'CAF',
    ER: 'ERI',
    DJ: 'DJI',
    SO: 'SOM',
    ZW: 'ZWE',
    ZM: 'ZMB',
    MW: 'MWI',
    BW: 'BWA',
    NA: 'NAM',
    SZ: 'SWZ',
    LS: 'LSO',
    MU: 'MUS',
    SC: 'SYC',
    CV: 'CPV',
    ST: 'STP',
    KM: 'COM',
    GM: 'GMB',
    GW: 'GNB',
    MR: 'MRT',
    TG: 'TGO',
    BJ: 'BEN',
    EH: 'ESH',
    SH: 'SHN',
    RE: 'REU',
    YT: 'MYT',
    AQ: 'ATA',
    BV: 'BVT',
    IO: 'IOT',
    TF: 'ATF',
    UM: 'UMI',
};
export function convertAlpha2ToAlpha3(alpha2Code) {
    var _a;
    if (!alpha2Code)
        return undefined;
    return (_a = countryCodeAlpha2ToAlpha3Map[alpha2Code.toUpperCase()]) !== null && _a !== void 0 ? _a : alpha2Code;
}
const countryCodeAlpha3ToAlpha2Map = Object.fromEntries(Object.entries(countryCodeAlpha2ToAlpha3Map).map(([a2, a3]) => [a3, a2]));
export function convertAlpha3ToAlpha2(alpha3Code) {
    var _a;
    if (!alpha3Code)
        return undefined;
    return (_a = countryCodeAlpha3ToAlpha2Map[alpha3Code.toUpperCase()]) !== null && _a !== void 0 ? _a : alpha3Code;
}
/**
 * The country-prefix check is skipped unless `expectedCountryAlpha2` is a
 * clean two-letter code, so an unresolved country never falsely rejects. An
 * empty IBAN is invalid; callers wanting a distinct "required" message check
 * presence first.
 */
export function validateIban(iban, expectedCountryAlpha2) {
    const normalized = electronicFormatIBAN(iban !== null && iban !== void 0 ? iban : '');
    if (!normalized || !isValidIBAN(normalized)) {
        return { valid: false, error: 'Enter a valid IBAN' };
    }
    if (expectedCountryAlpha2) {
        const expected = expectedCountryAlpha2.trim().toUpperCase();
        const ibanCountry = normalized.slice(0, 2);
        if (/^[A-Z]{2}$/.test(expected) && ibanCountry !== expected) {
            return {
                valid: false,
                error: `IBAN country (${ibanCountry}) does not match the selected country (${expected})`,
            };
        }
    }
    return { valid: true };
}
// ISO 9362 BIC: 8 (BBBBCCXX) or 11 (BBBBCCXXXXX) characters, uppercase
// letters and digits only. Characters 5-6 are the bank's country.
const BIC_REGEX = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
/**
 * A branch carries its own country's letters whoever owns the bank, so a
 * mismatch is a typo or a wrong-country pick (and Airwallex rejects it). A
 * malformed BIC or unknown country passes: those are the caller's rules, not
 * to be double-reported.
 */
export function validateBicCountry(swiftCode, expectedCountryAlpha2) {
    const bic = (swiftCode !== null && swiftCode !== void 0 ? swiftCode : '').trim().toUpperCase();
    const expected = (expectedCountryAlpha2 !== null && expectedCountryAlpha2 !== void 0 ? expectedCountryAlpha2 : '').trim().toUpperCase();
    if (!BIC_REGEX.test(bic) || !/^[A-Z]{2}$/.test(expected)) {
        return { valid: true };
    }
    const bicCountry = bic.slice(4, 6);
    if (bicCountry !== expected) {
        return {
            valid: false,
            error: `SWIFT code country (${bicCountry}) does not match the selected country (${expected})`,
        };
    }
    return { valid: true };
}
/**
 * The exact string `validateIban` checks, so a validated value is
 * alphanumeric as Increase's Swift `account_number` requires. Store this,
 * never the display-formatted input. Empty stays null, not ''.
 */
export function normalizeIban(iban) {
    return electronicFormatIBAN(iban !== null && iban !== void 0 ? iban : '') || null;
}
//# sourceMappingURL=utils.js.map